import { useEffect, useState } from "react";
import { client } from "../../sanityClient";
import GrainForm from "../forms/GrainForm";
import GrainTable from "../tables/GrainTable";
import GrainFilters from "../filters/GrainFilters";
import NewGrainForm from "../forms/NewGrainForm";
import DeleteConfirmModal from "../modals/DeleteConfirmModal";
import Toast from "../common/Toast";
import EditGrainForm from "../forms/EditGrainForm";

export default function GrainDashboard() {
    const [form, setForm] = useState({
        buyOrSell: "", grainId: "", quantity: "", rate: "", finalPrice: "", time: "", remarks: ""
    });
    const [grains, setGrains] = useState([]);
    const [grainNames, setGrainNames] = useState([]);
    const [showGrainModal, setShowGrainModal] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [allFilteredData, setAllFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [total, setTotal] = useState(0);
    const [filter, setFilter] = useState({ type: "All", grain: "All", from: "", to: "" });
    const [sortBy, setSortBy] = useState("created_desc");
    const [deleteModal, setDeleteModal] = useState({ show: false, id: null });
    const [deleteGrainModal, setDeleteGrainModal] = useState({ show: false, grain: null });
    const [toast, setToast] = useState({ show: false, type: "", message: "" });
    const [editingTransaction, setEditingTransaction] = useState(null);

    useEffect(() => { fetchGrains(); fetchGrainNames(); }, []);
    useEffect(() => { fetchTransactions(); }, [currentPage, rowsPerPage, filter, sortBy]);

    const fetchGrains = () => client.fetch(`*[_type == "grain"]{_id, grainName}`).then(setGrains);
    const fetchGrainNames = () => client.fetch(`*[_type == "grainTransaction"].grain->grainName`).then(d => setGrainNames([...new Set(d.filter(Boolean))]));

    const fetchTransactions = async () => {
        setLoading(true);
        const f = [];
        if (filter.type !== "All") f.push(`buyOrSell == "${filter.type}"`);
        if (filter.grain !== "All") f.push(`grain->grainName == "${filter.grain}"`);
        if (filter.from) f.push(`dateTime(time) >= dateTime("${filter.from}T00:00:00Z")`);
        if (filter.to) f.push(`dateTime(time) <= dateTime("${filter.to}T23:59:59Z")`);
        const query = `*[_type == "grainTransaction"${f.length ? " && " + f.join(" && ") : ""}] | order(_createdAt desc) { 
      _id, GTrID, buyOrSell, quantity, rate, finalPrice, time, remarks, _createdAt,
      grain->{grainName} 
    }`;

        let data = await client.fetch(query);

        if (sortBy === "created_desc") data.sort((a, b) => new Date(b._createdAt) - new Date(a._createdAt));
        if (sortBy === "date_desc") data.sort((a, b) => new Date(b.time) - new Date(a.time));
        if (sortBy === "date_asc") data.sort((a, b) => new Date(a.time) - new Date(b.time));
        if (sortBy === "amount_desc") data.sort((a, b) => b.finalPrice - a.finalPrice);
        if (sortBy === "amount_asc") data.sort((a, b) => a.finalPrice - b.finalPrice);

        setAllFilteredData(data);
        setTotal(data.length);
        const start = (currentPage - 1) * rowsPerPage;
        setTransactions(data.slice(start, start + rowsPerPage));
        setLoading(false);
    };

    const showToast = (type, message) => {
        setToast({ show: true, type, message });
        setTimeout(() => setToast({ show: false, type: "", message: "" }), 3000);
    };

    const generateGTrID = () => {
        const now = new Date();
        const pad = (n) => n.toString().padStart(2, "0");
        const ms = now.getMilliseconds().toString().padStart(3, "0");
        return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}${ms}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.buyOrSell || !form.grainId || !form.quantity || !form.rate || !form.time) {
            showToast("error", "Please fill all required fields");
            return;
        }
        if (submitting) return;
        setSubmitting(true);

        try {
            const newID = generateGTrID();
            await client.create({
                _type: "grainTransaction",
                GTrID: newID,
                buyOrSell: form.buyOrSell,
                grain: { _type: "reference", _ref: form.grainId },
                quantity: +form.quantity,
                rate: +form.rate,
                finalPrice: +form.finalPrice,
                time: new Date(form.time).toISOString(),
                remarks: form.remarks || "",
            });

            setForm({ buyOrSell: "", grainId: "", quantity: "", rate: "", finalPrice: "", time: "", remarks: "" });
            setCurrentPage(1);
            fetchTransactions();
            showToast("success", `Grain added! ID: ${newID}`);
        } catch (err) {
            console.error(err);
            showToast("error", "Failed to add grain");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = (id) => setDeleteModal({ show: true, id });
    const confirmDelete = async () => {
        await client.delete(deleteModal.id);
        setDeleteModal({ show: false, id: null });
        fetchTransactions();
        showToast("error", "Grain deleted");
    };

    const handleEditGrain = (grain) => {
        const newName = prompt(`Edit grain name:`, grain.grainName);
        if (newName && newName.trim() && newName.trim() !== grain.grainName) {
            // Check if grain with same name already exists
            client.fetch(`*[_type == "grain" && lower(grainName) == $name && _id != $id]`, {
                name: newName.trim().toLowerCase(),
                id: grain._id
            }).then(existing => {
                if (existing.length > 0) {
                    showToast("error", "A grain with this name already exists");
                } else {
                    client.patch(grain._id).set({ grainName: newName.trim() }).commit()
                        .then(() => {
                            fetchGrains();
                            showToast("success", "Grain updated successfully");
                        })
                        .catch(err => {
                            console.error(err);
                            showToast("error", "Failed to update grain");
                        });
                }
            });
        }
    };

    const handleDeleteGrain = (grain) => {
        setDeleteGrainModal({ show: true, grain });
    };

    const confirmDeleteGrain = async () => {
        const grain = deleteGrainModal.grain;

        // Check if grain has any transactions
        const transactions = await client.fetch(`*[_type == "grainTransaction" && grain._ref == $grainId]`, {
            grainId: grain._id
        });

        if (transactions.length > 0) {
            showToast("error", `Cannot delete "${grain.grainName}" because it has ${transactions.length} transaction(s). Delete all transactions first.`);
            setDeleteGrainModal({ show: false, grain: null });
            return;
        }

        try {
            await client.delete(grain._id);
            setDeleteGrainModal({ show: false, grain: null });
            fetchGrains();
            showToast("success", "Grain deleted successfully");
        } catch (err) {
            console.error(err);
            showToast("error", "Failed to delete grain");
        }
    };

    const handleClear = () => {
        setFilter({ type: "All", grain: "All", from: "", to: "" });
        setRowsPerPage(25);
        setSortBy("created_desc");
        setCurrentPage(1);
    };

    return (
        <div className="container-fluid mt-4">
            <GrainForm
                form={form}
                setForm={setForm}
                grains={grains}
                onSubmit={handleSubmit}
                onShowGrainModal={setShowGrainModal}
                onEditGrain={handleEditGrain}
                onDeleteGrain={handleDeleteGrain}
                submitting={submitting}
            />

            <GrainFilters
                filter={filter}
                setFilter={setFilter}
                grainNames={grainNames}
                onClear={handleClear}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                sortBy={sortBy}
                setSortBy={setSortBy}
            />

            <GrainTable
                data={transactions}
                allFilteredData={allFilteredData}
                loading={loading}
                currentPage={currentPage}
                totalPages={Math.ceil(total / rowsPerPage)}
                onPageChange={setCurrentPage}
                onDelete={handleDelete}
                onEdit={setEditingTransaction}
            />

            {showGrainModal && (
                <div className="modal d-block bg-dark bg-opacity-50" onClick={() => setShowGrainModal(false)}>
                    <div className="modal-dialog" onClick={e => e.stopPropagation()}>
                        <div className="modal-content">
                            <NewGrainForm
                                onClose={() => setShowGrainModal(false)}
                                onGrainCreated={() => { fetchGrains(); setShowGrainModal(false); }}
                                showToast={showToast}
                            />
                        </div>
                    </div>
                </div>
            )}

            <DeleteConfirmModal
                show={deleteModal.show}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteModal({ show: false, id: null })}
                title="Delete Transaction?"
                message="This action cannot be undone. Are you sure?"
            />

            <DeleteConfirmModal
                show={deleteGrainModal.show}
                onConfirm={confirmDeleteGrain}
                onCancel={() => setDeleteGrainModal({ show: false, grain: null })}
                title="Delete Grain"
                message={`Are you sure you want to delete "${deleteGrainModal.grain?.grainName}"? This will permanently remove the grain. Note: Grains with existing transactions cannot be deleted.`}
            />

            <Toast
                show={toast.show}
                type={toast.type}
                message={toast.message}
                onClose={() => setToast({ show: false, type: "", message: "" })}
            />

            {editingTransaction && (
                <EditGrainForm
                    transaction={editingTransaction}
                    onClose={() => setEditingTransaction(null)}
                    onUpdate={() => {
                        setEditingTransaction(null);
                        fetchTransactions();
                    }}
                />
            )}
        </div>
    );
}